import type { App, Environment, Platform } from '@hangar/shared';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { OptionSelect } from '@/components/OptionSelect';
import type { ResolvePreviewApi } from './useResolvePreview';

/** App / environment / platform / CI variables + the Resolve button. */
export function ResolveForm({ r, apps, environments }: { r: ResolvePreviewApi; apps: App[] | undefined; environments: Environment[] }) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <AppSelect value={r.appSlug} onChange={r.setAppSlug} apps={apps} />
      <EnvironmentSelect value={r.environment} onChange={r.setEnvironment} environments={environments} />
      <PlatformSelect value={r.platform} onChange={r.setPlatform} />
      <div className="flex min-w-[200px] flex-1 flex-col gap-1">
        <Label htmlFor="env-resolve-ci" className="text-xs text-subtle">
          CI variables (optional, comma-separated)
        </Label>
        <Input id="env-resolve-ci" data-testid="env-resolve-ci" value={r.ciVariables} onChange={(e) => r.setCiVariables(e.target.value)} placeholder="APP_ENV, EXPO_TOKEN" className="font-mono sm:text-[13px]" spellCheck={false} />
      </div>
      <Button data-testid="env-resolve-run" disabled={!r.ready} loading={r.isPending} onClick={() => void r.run()}>
        <Icon name="account_tree" size={18} />
        Resolve
      </Button>
    </div>
  );
}

function AppSelect({ value, onChange, apps }: { value: string; onChange: (v: string) => void; apps: App[] | undefined }) {
  return (
    <div className="flex min-w-[160px] flex-col gap-1">
      <Label htmlFor="env-resolve-app" className="text-xs text-subtle">
        App
      </Label>
      <OptionSelect
        id="env-resolve-app"
        data-testid="env-resolve-app"
        value={value}
        onValueChange={onChange}
        placeholder="Pick"
        options={(apps ?? []).map((a) => ({ value: a.slug, label: a.slug }))}
        className="font-mono sm:text-[13px]"
        contentClassName="font-mono"
      />
    </div>
  );
}

function EnvironmentSelect({ value, onChange, environments }: { value: string; onChange: (v: string) => void; environments: Environment[] }) {
  return (
    <div className="flex min-w-[140px] flex-col gap-1">
      <Label htmlFor="env-resolve-environment" className="text-xs text-subtle">
        Environment
      </Label>
      <OptionSelect id="env-resolve-environment" data-testid="env-resolve-environment" value={value} onValueChange={onChange} placeholder="Pick" options={environments.map((env) => ({ value: env.slug, label: env.name }))} />
    </div>
  );
}

function PlatformSelect({ value, onChange }: { value: Platform; onChange: (p: Platform) => void }) {
  return (
    <div className="flex min-w-[120px] flex-col gap-1">
      <Label htmlFor="env-resolve-platform" className="text-xs text-subtle">
        Platform
      </Label>
      <OptionSelect
        id="env-resolve-platform"
        data-testid="env-resolve-platform"
        value={value}
        onValueChange={(v) => onChange(v as Platform)}
        options={[
          { value: 'android', label: 'Android' },
          { value: 'ios', label: 'iOS' },
        ]}
      />
    </div>
  );
}
